import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { useAuth } from "@/auth/useAuth";
import { dailyApi } from "@/features/daily/api/daily.api";
import { useDailyCopy } from "@/features/daily/hooks/use-daily-copy";
import { useDailyQueryState } from "@/features/daily/hooks/use-daily-query-state";
import { useDailyFilters } from "@/features/daily/hooks/use-daily-filters";
import type { OrdersAnalyticsFilters } from "@/features/orders-analytics/types/orders-analytics.types";
import { dailyDates } from "@/features/daily/components/daily-dates";
import { DailyResourceStatus, useDailyResource } from "@/features/daily/hooks/use-daily-resource";
import { DailySectionState, DailyStateBlock } from "@/features/daily/components/DailyStateBlock";
import { DailyHeaderMeta } from "@/features/daily/components/DailyHeaderMeta";
import { SkipLink } from "@/features/daily/components/SkipLink";
import { SourceBar } from "@/features/daily/components/SourceBar";
import { DayFilters } from "@/features/daily/components/diagnostic/DayFilters";
import { DayNavigator } from "@/features/daily/components/diagnostic/DayNavigator";
import { DaySummaryCards } from "@/features/daily/components/diagnostic/DaySummaryCards";
import { daySummaryViewModel, isDaySummaryDto } from "@/features/daily/components/diagnostic/day-summary-view-model";
import { ExportButton } from "@/features/daily/components/diagnostic/ExportButton";
import { OperationDetailModal } from "@/features/orders-analytics/components/OperationDetailModal";
import { QueueTable } from "@/features/daily/components/diagnostic/QueueTable";
import { QueueMenu } from "@/features/daily/components/diagnostic/QueueMenu";
import { SearchBox } from "@/features/daily/components/diagnostic/SearchBox";
import {
    QueueSortKey,
    diagnosticViewModel,
    isQueuePageDto,
} from "@/features/daily/components/diagnostic/diagnostic-view-model";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";
import "@/features/daily/styles/daily.css";

const BODY_ID = "daily-diagnostic-main";
const POLL_MS = 60_000;

const SECTION_STATE: Record<DailyResourceStatus, DailySectionState> = {
    idle: "loading",
    loading: "loading",
    ok: "ok",
    partial: "partial",
    stale: "stale",
    error: "error",
    forbidden: "forbidden",
};

export function DiagnosticPage() {
    const { state: authState } = useAuth();
    const { query, search, notice, set, resetPaginationForExpiredCursor, dismissNotice } = useDailyQueryState();
    const { status: copyStatus, copy } = useDailyCopy();

    const viewerId = authState.user === null ? "" : authState.user.id;
    const queue = diagnosticViewModel.readQueue(query.queue);
    const [sortKey, setSortKey] = useState<QueueSortKey>("server");
    // The search term never enters the URL (SC-025), so the resource is keyed by a
    // counter instead: a new search is a new question and must reach the server,
    // while nothing identifying anyone is written into a cache key.
    const [searchEpoch, setSearchEpoch] = useState(0);
    const triggerRef = useRef<HTMLElement | null>(null);

    // Enmienda C14: one builder, used by the table AND by the export below.
    const params = diagnosticViewModel.queueParams(query, queue);

    // D105: the header range also moves the day queue — to the last day of the range
    // (never past now), so the navigator starts inside what the header says.
    const moveDayIntoRange = useCallback((next: OrdersAnalyticsFilters) => {
        if (queue !== "day") return {};
        if (next.comparisonPeriod === "last_full_month" || next.comparisonPeriod === "previous_range") {
            return { date: dailyDates.madridDateOf(Math.min(next.dateTo, Date.now())) };
        }
        return { date: null };
    }, [queue]);
    const { filters, onFiltersChange } = useDailyFilters(query, set, "today", moveDayIntoRange);

    const queueResource = useDailyResource(
        {
            resource: "diagnostic-queue",
            scope: params.scope,
            // CH-8: the counter restarts with the page while the cache outlives it, so
            // the key also names WHICH term was searched — by digest, never in clear.
            query: {
                ...params,
                searchApplied: searchEpoch === 0 ? "" : String(searchEpoch),
                searchDigest: search === "" ? "" : diagnosticViewModel.searchDigest(search),
            },
        },
        (signal) => {
            const options = { signal, search: search === "" ? undefined : search };
            // D106: the day queue shows every operation of the day, not one page of it.
            return queue === "day" ? dailyApi.getWholeQueue(params, options) : dailyApi.getQueue(params, options);
        },
        { identity: viewerId, isData: isQueuePageDto, pollMs: POLL_MS },
    );

    // D106: the selected day's KPIs. A separate read — the table pages, the summary does not.
    const summaryParams = { date: params.date ?? "", scope: params.scope, types: params.types };
    const summaryResource = useDailyResource(
        { resource: "diagnostic-day-summary", scope: summaryParams.scope, query: summaryParams },
        (signal) => dailyApi.getDaySummary(summaryParams, { signal }),
        { identity: viewerId, isData: isDaySummaryDto, pollMs: POLL_MS, enabled: queue === "day" },
    );

    const operationId = query.operationId;

    // Enmienda C8: a signed cursor expires. The only honest recovery is page one with
    // a notice — never a silent dead end and never a page of somebody else's results.
    const queueError = queueResource.error;
    useEffect(() => {
        if (queueError !== null && queueError.code === "cursor_expired") resetPaginationForExpiredCursor();
    }, [queueError, resetPaginationForExpiredCursor]);

    const openOperation = useCallback((id: string, trigger: HTMLElement): void => {
        triggerRef.current = trigger;
        set({ operationId: id });
    }, [set]);

    // FR-017/SC-017: closing the evidence removes ONLY the operation from the URL.
    // The queue, the page, the scope and the window are the operator's context and
    // survive untouched.
    const closeOperation = useCallback((): void => {
        set({ operationId: null });
    }, [set]);

    const runSearch = useCallback((term: string): void => {
        // A new question starts on its first page: a cursor minted for another term is
        // meaningless for this one. An account's history is answered by the day queue's
        // filters (B3), so an email search moves there — the URL gains `queue=day`,
        // never the address itself.
        const toDay = diagnosticViewModel.isEmailTerm(term);
        set(toDay
            ? { search: term, queue: "day", stage: null, cursor: null, page: null }
            : { search: term, cursor: null, page: null });
        setSearchEpoch((current) => current + 1);
    }, [set]);

    const dto = queueResource.data;
    const emailSearch = search !== "" && diagnosticViewModel.isEmailTerm(search);
    // D99: a refused term is an instruction for the operator, not a queue failure.
    const searchError = diagnosticViewModel.searchError(queueResource.error);
    const context = dto === null ? null : diagnosticViewModel.context(dto);
    const rows = dto === null ? [] : diagnosticViewModel.sortRows(diagnosticViewModel.rows(dto), sortKey);
    const truncation = dto === null ? null : diagnosticViewModel.truncationNotice(dto);
    const busy = queueResource.status === "loading" || queueResource.status === "idle";
    const sectionState: DailySectionState = dto !== null && busy ? "ok" : SECTION_STATE[queueResource.status];
    const nav = dto === null || queue !== "day" ? null : diagnosticViewModel.dayNavigator(query, dto);

    const summary = emailSearch
        ? `Correo: ${search} · últimos 365 días · operaciones creadas por esa cuenta.`
        : nav !== null
            ? `Día seleccionado: ${nav.dateLabel} · Europe/Madrid · ${nav.completenessLabel}.`
            : context === null ? diagnosticViewModel.queueLabel(queue) : context.queueDescription;

    return (
        <Shell title="Diagnostic" subtitle="Investigar operaciones">
            <div className="daily-page daily-page--fit">
                <SkipLink targetId={BODY_ID} />
                <PageHeader
                    title="Investigar operaciones"
                    lede="Histórico, conteos y búsqueda por operación o correo."
                    right={(
                        <DailyHeaderMeta
                            chips={null}
                            filters={filters}
                            onFiltersChange={onFiltersChange}
                            busy={busy}
                            onRefresh={() => { queueResource.refresh(); summaryResource.refresh(); }}
                        />
                    )}
                />

                <div className="daily-body" id={BODY_ID} tabIndex={-1}>
                    {queue !== "day" || emailSearch ? null : summaryResource.data === null ? (
                        summaryResource.status === "error" || summaryResource.status === "forbidden" ? (
                            <p className="daily-small daily-muted" role="status">No se pudieron cargar las métricas del día.</p>
                        ) : null
                    ) : (
                        <DaySummaryCards
                            title={`Métricas del ${nav === null ? "día" : nav.dateLabel}`}
                            tiles={daySummaryViewModel.tiles(summaryResource.data)}
                        />
                    )}

                    {/* D131: one toolbar right above the table — day tabs, day, search, queues, export. */}
                    <div className="daily-toolbar">
                        {queue === "day" || emailSearch ? (
                            <DayFilters
                                tabs={diagnosticViewModel.dayFilters(query, dto, ["all", "problems", "cancelled"])}
                                chips={diagnosticViewModel.dayFilters(query, dto, ["in_progress", "overdue"])}
                            />
                        ) : null}
                        {nav === null ? null : (
                            <DayNavigator
                                nav={nav}
                                disabled={emailSearch}
                                onPickDate={(date) => set({ date, cursor: null, page: null })}
                            />
                        )}
                        <SearchBox value={search} onSearch={runSearch} error={searchError} />
                        <QueueMenu
                            label={queue === "day" ? "Más colas" : diagnosticViewModel.queueLabel(queue)}
                            items={diagnosticViewModel.queueTabs.map((tab) => ({
                                kind: tab,
                                label: diagnosticViewModel.queueLabel(tab),
                                href: diagnosticViewModel.queueTabHref(query, tab),
                                active: tab === queue,
                            }))}
                        />
                        {/* D36/P3-3: the server authorises an export like a mutation; offering it to
                            a reader who would get 403 is an action that cannot be completed. The
                            CSV never carries the account email (D99). */}
                        {dto === null || dto.permissions?.canOperate !== true ? null : (
                            <ExportButton params={params} search={search} fileName={`daily-${queue}.csv`} />
                        )}
                    </div>

                    {notice === null ? null : (
                        <div className="daily-state daily-state--info" role="status">
                            <p className="daily-state__title">Tu posición en esta cola caducó</p>
                            <p className="daily-state__body">
                                El enlace de página en el que estabas ya no es válido, así que esta es de nuevo la primera página.
                            </p>
                            <button type="button" className="daily-button daily-state__retry" onClick={dismissNotice}>
                                Entendido
                            </button>
                        </div>
                    )}

                    <DailyStateBlock
                        state={sectionState}
                        label="Cola"
                        detail={searchError ?? (queueResource.error === null ? undefined : queueResource.error.message)}
                        onRetry={queueResource.refresh}
                    >
                        {dto === null || context === null ? null : (
                            <section className="daily-panel" aria-labelledby="daily-queue-title">
                                {truncation === null ? null : (
                                    <p className="daily-notice" role="status">{truncation}</p>
                                )}

                                <QueueTable
                                    heading={(
                                        <div className="daily-queue-heading">
                                            <h2 id="daily-queue-title">{emailSearch ? "Operaciones del correo" : context.tableTitle}</h2>
                                            <span className="daily-small daily-muted">
                                                {queue === "day"
                                                    ? dailyFormat.counted(dto.totalItems ?? rows.length, "operación", "operaciones")
                                                    : dto.totalItems === null
                                                        ? `${rows.length} en esta página`
                                                        : `${dailyFormat.count(dto.totalItems)} coincidencias · ${rows.length} en esta página`}
                                            </span>
                                        </div>
                                    )}
                                    key={`${queue}|${params.date ?? ""}|${params.filter ?? ""}|${searchEpoch}`}
                                    caption={`${context.tableTitle} — ${queue === "day" || emailSearch ? "las más recientes primero" : "las más antiguas primero"}; ordenar solo reordena esta página`}
                                    regionLabel={`${emailSearch ? "Operaciones del correo" : context.tableTitle}, lista desplazable`}
                                    rows={rows}
                                    emptyMessage={diagnosticViewModel.emptyMessage()}
                                    sortKey={sortKey}
                                    newestFirst={queue === "day" || emailSearch}
                                    onSortChange={setSortKey}
                                    onOpen={openOperation}
                                    onCopy={copy}
                                    onFilterEmail={runSearch}
                                />
                                <p className="daily-copy-feedback" role="status" aria-live="polite">
                                    {operationId === null ? copyStatus : ""}
                                </p>

                                {queue === "day" ? null : (
                                <div className="daily-table-footer">
                                    <span className="daily-small daily-muted">Página {query.page}</span>
                                    <div className="daily-actions">
                                        {query.page > 1 ? (
                                            <button
                                                type="button"
                                                className="daily-button"
                                                onClick={() => set({ page: null, cursor: null })}
                                            >
                                                Volver a la primera página
                                            </button>
                                        ) : null}
                                        {dto.nextCursor === null ? null : (
                                            <button
                                                type="button"
                                                className="daily-button"
                                                onClick={() => set({ page: query.page + 1, cursor: dto.nextCursor })}
                                            >
                                                Página siguiente
                                            </button>
                                        )}
                                    </div>
                                </div>
                                )}

                            </section>
                        )}
                    </DailyStateBlock>

                    {/* F-2/D95: a page link that the server refuses leaves no rows to page
                        from, so the only way back cannot live inside the results. */}
                    {dto === null && queueResource.error !== null && query.cursor !== null ? (
                        <div className="daily-actions">
                            <button
                                type="button"
                                className="daily-button"
                                onClick={() => set({ page: null, cursor: null })}
                            >
                                Volver a la primera página
                            </button>
                        </div>
                    ) : null}

                    <SourceBar
                        fetchedAt={queueResource.meta.fetchedAt}
                        sourceAsOf={queueResource.meta.sourceAsOf}
                        stale={queueResource.meta.stale}
                        evidenceLabel={null}
                        evidenceTip={[
                            summary,
                            ...(context === null ? [] : [`Evidencia: ${context.qualityLabel} · ${context.policyLabel}`, ...context.reasons]),
                        ]}
                        note="La búsqueda histórica no modifica la salud actual."
                    />

                    {operationId === null ? null : (
                        // D120: the app's own detail modal — the one the drilldown uses, in its
                        // legacy mode (it loads the operation itself) — with previous/next across
                        // the rows listed on screen.
                        <OperationDetailModal
                            operationId={operationId}
                            operationIds={rows.map((row) => row.operationId)}
                            onNavigate={(next) => set({ operationId: next })}
                            onClose={closeOperation}
                            restoreFocusTo={triggerRef.current}
                        />
                    )}
                </div>
            </div>
        </Shell>
    );
}
